import {StateController} from "@lit-app/state/src/state-controller";
import {html, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {ifDefined} from "lit/directives/if-defined.js";
import {when} from "lit/directives/when.js";
import {container} from "tsyringe";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, filter, map, takeUntil} from "rxjs/operators";
import {appState} from "../../app.state";
import {FiltersDTO} from "../../domain/dtos/filters.dto";
import {Employee} from "../../domain/entities/employee.entity";
import {Entity} from "../../domain/entities/entity";
import {IssueStatus} from "../../domain/entities/issue-status.entity";
import {IssueType} from "../../domain/entities/issue-type.entity";
import {Keyword} from "../../domain/entities/keyword.entity";
import {Project} from "../../domain/entities/project.entity";
import {EmployeeRepository} from "../../domain/repositories/employee.repository";
import {IssueStatusRepository} from "../../domain/repositories/issue-status.repository";
import {IssueTypeRepository} from "../../domain/repositories/issue-type.repository";
import {KeywordRepository} from "../../domain/repositories/keyword.repository";
import {ProjectRepository} from "../../domain/repositories/project.repository";
import {AbstractLitElement} from "../abstract-lit.element";
import {translate} from "lit-translate";
import {isFilterValueExcluded} from "../../helpers/issue-type-filter-scope.helper";
import {ComboboxItem} from "../_inputs/ha-combobox/combobox-item.interface";

/**
 * The three allowed changedWithin values (E4, operator + Anke — exactly three, no more).
 * A plain literal list, not a translate() lookup — same precedent as
 * ha-form-knowledge-filters.component.ts's KNOWLEDGE_TYPE_ITEMS.
 */
const CHANGED_WITHIN_ITEMS: ReadonlyArray<ComboboxItem> = [
    {id: `1h`, title: `Letzte Stunde`, payload: `1h`},
    {id: `1d`, title: `Letzter Tag`, payload: `1d`},
    {id: `7d`, title: `Letzte 7 Tage`, payload: `7d`},
];

@customElement(`ha-form-issue-filters`)
export class HaFormIssueFiltersComponent extends AbstractLitElement {

    @state()
    private _filters: Record<string, any> | null = null;

    /**
     * Target AppState slot this filter form reads from and writes to. Defaults to
     * the ToDo `filters` slot so every existing ToDo view keeps working unchanged
     * (Failure Condition (a)); the Resource-Management view sets it to
     * `resourceFilters` (O2D-PRD-100 T3.5) so its filter machinery is fully
     * isolated from the ToDo slot.
     */
    public filterSlot: 'filters' | 'resourceFilters' = 'filters';

    /**
     * Restricts the issue-type filter's selectable options to what the calling view
     * can structurally contain (e.g. /tasklist excludes absences/holidays,
     * /absence-list allows only those) — set by the parent view, e.g. via
     * buildTasklistIssueTypeItemFilter(). Default null = unrestricted (every type).
     */
    @property({type: Object})
    public issueTypeItemFilter: ((item: ComboboxItem) => boolean) | null = null;

    private _searchTextSubject = new Subject<string>();
    protected override readonly _disconnected$ = new Subject<void>();

    constructor() {
        super();
        new StateController(this, appState);
    }

    public connectedCallback() {
        super.connectedCallback();
        this._toEntities(appState[this.filterSlot]).then(filters => this._filters = filters);

        appState.subscribe((key) => {
            if (key === this.filterSlot) {
                this._toEntities(appState[this.filterSlot]).then(filters => {
                    this._filters = filters;
                });
            }
        });

        this._searchTextSubject.pipe(
            map(value => value.trim()),
            filter(value => value.length >= 3 || value.length === 0),
            debounceTime(400),
            distinctUntilChanged(),
            takeUntil(this._disconnected$)
        ).subscribe(searchText => {
            this._onFilterChanged('searchText', { 
                detail: { value: searchText || null }
            } as CustomEvent<{ value: any }>);
        });
    }

    public disconnectedCallback() {
        super.disconnectedCallback();
        this._disconnected$.next();
        this._disconnected$.complete();
    }

    /**
     * When issueTypeItemFilter changes (e.g. switching between /tasklist and
     * /absence-list) and the currently-set type filter value is no longer allowed
     * by it — including a value hydrated from localStorage on load — reset it
     * instead of leaving a filter selected that would structurally always yield
     * zero results, both in the UI and in the persisted appState[filterSlot].
     */
    protected updated(changedProperties: PropertyValues): void {
        super.updated(changedProperties);

        if (!changedProperties.has('issueTypeItemFilter')) {
            return;
        }

        const currentType = this._filters?.issueType;
        if (isFilterValueExcluded(currentType, this.issueTypeItemFilter)) {
            this._onFilterChanged('issueType', {detail: {value: undefined}} as CustomEvent<{ value: any }>);
        }
    }

    protected render(): unknown {
        return html`
            <ha-input-text class="max9999-flexcolumn" 
                           label=${translate('ha-sidebar-filter.search-text')}
                           .value=${this._filters?.searchText ?? ''}
                           @value-changed=${this._onSearchTextInput}
                           with-clear-button
            ></ha-input-text>
            <ha-combobox class="max9999-flexcolumn " data-type="creator"
                            label=${translate('ha-sidebar-filter.creator')}
                            .entity=${Employee}
                            .value=${ifDefined(this._filters?.creator)}
                            @value-changed=${this._onFilterChanged.bind(this, `creator`)}
                            with-clear-button
                            with-filter
            ></ha-combobox>
            <ha-combobox-multi class="max9999-flexcolumn" data-type="assignee"
                                label=${translate('ha-sidebar-filter.assignee')}
                                .entity="${Employee}"
                                .value=${this._filters?.assignees ?? []}
                                @value-changed=${this._onFilterChanged.bind(this, `assignees`)}
                                with-clear-button
                                with-filter
            ></ha-combobox-multi>
            <ha-combobox class="max9999-flexcolumn" data-type="type"
                            label=${translate('ha-sidebar-filter.type')}
                            .entity=${IssueType}
                            .itemFilter=${this.issueTypeItemFilter}
                            .value=${ifDefined(this._filters?.issueType)}
                            @value-changed=${this._onFilterChanged.bind(this, `issueType`)}
                            with-clear-button
                            with-filter
            ></ha-combobox>
            <ha-combobox class="max9999-flexcolumn" data-type="board"
                            label=${translate('ha-sidebar-filter.aggregation-dailys-projects')}
                            .entity=${Project}
                            .value=${ifDefined(this._filters?.board)}
                            @value-changed=${this._onFilterChanged.bind(this, `board`)}
                            with-clear-button
                            with-filter
            ></ha-combobox>
            <ha-combobox-multi class="max9999-flexcolumn" data-type="label" theme="visible"
                                label=${translate('general.label')}
                                .entity=${Keyword}
                                .value=${this._filters?.keywords ?? []}
                                @value-changed=${this._onFilterChanged.bind(this, `keywords`)}
                                with-clear-button
                                with-filter
            ></ha-combobox-multi>
            <ha-combobox-multi class="max9999-flexcolumn" data-type="status" theme="visible"
                                label=${translate('general.status')}
                                .entity=${IssueStatus}
                                .value=${ifDefined(this._filters?.issueStatus ?? [])}
                                @value-changed=${this._onFilterChanged.bind(this, `issueStatus`)}
                                with-clear-button
                                with-filter
            ></ha-combobox-multi>
            ${when(this.filterSlot === 'filters', () => html`
                <ha-combobox-multi class="max9999-flexcolumn" data-type="changed-by"
                                    label=${translate('ha-sidebar-filter.changed-by')}
                                    .entity="${Employee}"
                                    .value=${this._filters?.changedBy ?? []}
                                    @value-changed=${this._onFilterChanged.bind(this, `changedBy`)}
                                    with-clear-button
                                    with-filter
                ></ha-combobox-multi>
                <ha-combobox class="max9999-flexcolumn" data-type="changed-within"
                                label=${translate('ha-sidebar-filter.changed-within')}
                                .items=${CHANGED_WITHIN_ITEMS as ComboboxItem[]}
                                .value=${ifDefined(this._resolveChangedWithinItem(this._filters?.changedWithin))}
                                @value-changed=${this._onChangedWithinChanged}
                                with-clear-button
                ></ha-combobox>
            `)}
        `;
    }

    /**
     * Unwraps the ComboboxItem the static-items combobox emits (items-mode dispatches
     * the whole item, unlike entity-mode) down to its plain '1h'|'1d'|'7d' payload before
     * delegating to the shared handler — _filters.changedWithin must stay the plain
     * string FiltersDTO declares, not a ComboboxItem.
     */
    private _onChangedWithinChanged = (evt: CustomEvent<{ value: ComboboxItem | null }>): void => {
        this._onFilterChanged('changedWithin', {
            detail: {value: evt.detail.value?.payload}
        } as CustomEvent<{ value: any }>);
    }

    /**
     * Maps the stored plain '1h'|'1d'|'7d' string back to the ComboboxItem
     * ha-combobox's items-mode `.value` binding needs — same workaround
     * ha-form-knowledge-filters.component.ts's `_resolveTypeItem()` already uses for the
     * identical situation. Binding the raw string directly renders a BLANK label:
     * ha-combobox._renderLabel() reads `.title` off `.value` itself in items-mode
     * (`(this.value as ComboboxItem).title`), not off its internal
     * `_selectedComboboxItem` — a raw string has no `.title`. Caught in the browser
     * (Playwright), not by a unit test: the mocked FilterHelper/lit-translate stack
     * never renders the real ha-combobox this depends on.
     */
    private _resolveChangedWithinItem(changedWithin: string | undefined): ComboboxItem | undefined {
        return CHANGED_WITHIN_ITEMS.find(item => item.payload === changedWithin);
    }

    private _onFilterChanged(propertyKey: string, evt: CustomEvent<{ value: any }>): void {
        const filters = {...this._filters};
        const filterValue = evt.detail.value;

        switch (true) {
            case Array.isArray(filterValue):
                (filterValue.length > 0)
                    ? filters[propertyKey] = filterValue
                    : delete filters[propertyKey];
                break;
            case ((filterValue === undefined || filterValue === null || filterValue === false)):
                if (propertyKey in filters) {
                    delete filters[propertyKey];
                }
                break;
            default:
                filters[propertyKey] = filterValue;
        }

        this._filters = filters;
        appState[this.filterSlot] = this._updateFiltersAtAppState(filters);
    }

    private async _toEntities(filters: FiltersDTO): Promise<Record<string, any>> {
        const dto: Record<string, any> = {};
        const employeeRepository = container.resolve(EmployeeRepository);
        const keywordRepository = container.resolve(KeywordRepository);
        for (const [key, value] of Array.from(Object.entries(filters).filter(([key]) => key !== `uid`))) {
            switch (key) {
                case `board`:
                    dto.board = await container.resolve(ProjectRepository).findOneById(value);
                    break;
                case `creator`:
                    dto.creator = await employeeRepository.findOneById(value);
                    break;
                case `assignees`:
                    dto.assignees = await Promise.all(((value as Array<string>) ?? []).map(a => employeeRepository.findOneById(a)));
                    break;
                case `issueType`:
                    dto.issueType = await container.resolve(IssueTypeRepository).findOneById(value);
                    break;
                case `keywords`:
                    dto.keywords = await Promise.all(((value as Array<string>) ?? []).map(k => keywordRepository.findOneById(k)));
                    break;
                case `issueStatus`:
                    dto.issueStatus = await container.resolve(IssueStatusRepository).findById(Array.isArray(value) ? value : [value]);
                    break;
                case `hideTimeRange`:
                    dto.hideTimeRange = !!value
                    break;
                case `searchText`:
                    dto.searchText = value as string;
                    break;
                case `changedBy`:
                    // 9.7: a stale employee uid resolves to null — filtered out here so
                    // a non-entity never travels back to appState/the backend unchanged.
                    dto.changedBy = (await Promise.all(((value as Array<string>) ?? []).map(a => employeeRepository.findOneById(a))))
                        .filter((employee): employee is Employee => employee !== null);
                    break;
                case `changedWithin`:
                    dto.changedWithin = value as string;
                    break;
            }
        }

        return dto;
    }

    private _onSearchTextInput = (evt: CustomEvent<{ value: string }>): void => {
        this._searchTextSubject.next(evt.detail.value);
    }

    private _updateFiltersAtAppState(filters: Record<string, any>): FiltersDTO {
        const filtersAtAppState: Array<[keyof FiltersDTO, any]> = [];
        for (const [key, value] of Object.entries(filters) as [keyof FiltersDTO, any]) {
            filtersAtAppState.push([key, (Array.isArray(value))
                ? value.map(item => item instanceof Entity ? item.uid : item)
                : value instanceof Entity ? value.uid : value
            ]);
        }
        return Object.fromEntries(filtersAtAppState) as FiltersDTO;
    }
}